import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { RxTemplateService } from "../../api/rxtemplate";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import ConfirmModal from "../../components/ConfirmModal";

interface RxTemplate {
  id: string;
  prescriptionName: string;
  medication: string[];
  notes: string;
  description?: string;
  duration?: string;
  dosage?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
}

interface PaginationData {
  curPage: number;
  perPage: number;
  sortBy: string;
  direction: string;
  whereClause: Array<{
    key: string;
    value: string;
    operator: string;
  }>;
}

const defaultPayload: PaginationData = {
  curPage: 1,
  perPage: 10,
  sortBy: "created_on",
  direction: "desc",
  whereClause: [],
};

const RxTemplateList: React.FC = () => {
  const [rxTemplates, setRxTemplates] = useState<RxTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [payload, setPayload] = useState<PaginationData>(defaultPayload);
  const [total, setTotal] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));
  const fetchedRef = useRef(false);

  const fetchRxTemplates = async () => {
    setLoading(true);
    try {
      const paginationData: PaginationData = {
        ...payload,
        whereClause: searchTerm
          ? [{ key: "prescriptionName", value: searchTerm, operator: "contains" }]
          : [],
      };
      const response = await RxTemplateService.getRxTemplates(paginationData);
      const data = response.data || {};
      setRxTemplates(data.data || []);
      setTotal(data.total || data.count || 0);
    } catch (error) {
      console.error("Failed to fetch RxTemplates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchRxTemplates();
  }, []);

  useEffect(() => {
    fetchRxTemplates();
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    searchTerm,
  ]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPayload((prev) => ({ ...prev, curPage: 1 }));
    fetchRxTemplates();
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      await RxTemplateService.removeRxTemplate(deleteId);
      toast.success("RxTemplate deleted successfully!");
      fetchRxTemplates();
    } catch (error) {
      toast.error("Failed to delete RxTemplate");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Rx Templates
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Manage prescription templates
                  </p>
                </div>
                <Link
                  to="/rxtemplates/create"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  + Create Template
                </Link>
              </div>

              <form
                onSubmit={handleSearch}
                className="flex flex-col sm:flex-row gap-4 mb-4"
              >
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by prescription name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setPayload((prev) => ({ ...prev, curPage: 1 }));
                      fetchRxTemplates();
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Clear
                  </button>
                )}
              </form>

              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-medium">{total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Prescription Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Medication
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Notes
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Created On
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rxTemplates.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={5}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      rxTemplates.map((template, index) => (
                        <motion.tr
                          key={template.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {template.prescriptionName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            <div className="text-sm text-gray-600">
                              {template.medication.slice(0, 2).join(", ")}
                              {template.medication.length > 2 && (
                                <span className="text-gray-400">
                                  {" "}
                                  +{template.medication.length - 2} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            <div className="text-sm text-gray-600 max-w-xs truncate">
                              {template.notes}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {formatDate(template.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <Link
                                to={`/rxtemplates/edit/${template.id}`}
                                className="text-green-600 hover:underline hover:text-green-800 transition-colors duration-200"
                              >
                                Edit
                              </Link>
                              <button
                                onClick={() => handleDelete(template.id)}
                                className="text-red-600 hover:underline hover:text-red-800 transition-colors duration-200"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <hr />
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showDeleteModal}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Delete RxTemplate"
                message="Are you sure you want to delete this RxTemplate? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
              />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default RxTemplateList;
